import { MutableRefObject, useCallback } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { Node, XYPosition } from 'reactflow';
import { OperatingModelForPartners } from '@components/EntityDrawers/constants/common';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import { AssetKeys } from '@components/EntityDrawers/drawers/Assessment/keys';
import useEntityVisibilityCheck from '@components/EntityDrawers/hooks/useEntityVisibilityCheck';
import useSourceNode from '@components/EntityDrawers/hooks/useSourceNode';
import { emitNodeSaved } from '@components/EntityDrawers/nodeSaveObservable';
import useStore from '@components/MainStage/store';
import { AssetsSubLayerTypes } from '@constants/canvas/general';
import {
  CiaSubLayers,
  subLayerParent,
  SubLayerTypes,
} from '@constants/canvas/layers';
import { DateFormats } from '@constants/dates';
import { EditNodeDrawerMap } from '@constants/entities/drawers';
import { ISelectOption } from '@constants/entities/ui';
import { useProject } from '@context/Project/ProjectProvider';
import { useToast } from '@context/Toast/ToastProvider';
import { useAppDispatch } from '@hooks/store';
import useAssets from '@hooks/useAssets';
import useCurrentAsset from '@hooks/useCurrentAsset';
import useProjectId from '@hooks/useProjectId';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { nodesApi, useCreateProjectNodeMutation } from '@store/services/nodes';
import {
  AssessmentMetaData,
  ICreateNodeRequest,
  IUpdateNodeRequest,
  NodeDTO,
} from '@store/services/nodes/types';
import {
  projectsApi,
  useRemoveProjectOwnerMutation,
} from '@store/services/projects';
import { QueryTags } from '@store/services/tags';
import { add, prepareCia } from '@utils/drawers';
import {
  getNodeTypeTypeByForSubLayer,
  parseErrorResponse,
} from '@utils/helpers';
import { Tabs } from '@views/Insights/types';
import dayjs from 'dayjs';

export type NodeSaveOptions<T extends FieldValues, M> = {
  editMode?: boolean;
  form: UseFormReturn<T>;
  transformMetaDataToRequestBody: (
    values: T,
    context: { subLayer: SubLayerTypes; asset: NodeDTO<AssessmentMetaData> },
  ) => Record<keyof M, any>;
  onUpdate: (
    body: IUpdateNodeRequest,
    options?: { closeDrawer?: boolean },
  ) => Promise<void>;
  drawerEditingNodeIdRef: MutableRefObject<string | null>;
};

const useNodeSave = <T extends FieldValues, M>({
  form: { getValues, setError },
  editMode,
  transformMetaDataToRequestBody,
  onUpdate,
  drawerEditingNodeIdRef,
}: NodeSaveOptions<T, M>) => {
  const projectId = useProjectId(true);
  const setActiveNode = useStore((s) => s.setActiveNode);

  const {
    hideDrawer,
    setDrawer,
    toolbox: { mode },
    setHasChanges,
    insightsTab,
    isInsightsMode,
  } = useProject();

  const { showToast } = useToast();
  const sourceNodeFromHook = useSourceNode();
  const dispatch = useAppDispatch();
  const { isNewAssetMode, resetNewAssetMode, selectAsset, selectedAssetId } =
    useAssets();
  const { dto: asset } = useCurrentAsset();

  const { checkIsEntityBecameHidden } = useEntityVisibilityCheck();
  const { saveResponseToRedux } = useSaveEntitiesResponse();

  const [createNode, { isLoading: isCreating }] =
    useCreateProjectNodeMutation();
  const [removeOwner] = useRemoveProjectOwnerMutation();

  const resolveCanvasNodeForSave = useCallback((): Node | undefined => {
    const state = useStore.getState();
    const byRefId = drawerEditingNodeIdRef.current;
    if (byRefId) {
      const byId = state.getNodeById(byRefId);
      if (byId?.parentNode) return byId;
    }
    const selected = state.getActiveNode();
    if (selected?.parentNode) return selected;
    return undefined;
  }, [drawerEditingNodeIdRef]);

  const prepareDraftBody = (
    id: string,
    position: XYPosition,
    canvasNode: Pick<Node, 'parentNode'>,
  ): IUpdateNodeRequest => {
    const values = getValues();

    const isPartnersEnabled = OperatingModelForPartners.includes(
      values[CommonKeys.OperationalModel],
    );

    const parentNodeLayer = canvasNode.parentNode ?? '';

    const nodeBody: IUpdateNodeRequest = {
      nodeId: id,
      assetId: selectedAssetId || undefined,

      mode,

      is_reported: values[CommonKeys.IsReported] ?? true,

      name: values[CommonKeys.Name] || '',
      requirement_owner:
        values[CommonKeys.Owners]?.map(
          ({ value }: { value: string }) => value,
        ) ?? [],

      domains_id:
        values[CommonKeys.Domains]?.map(({ value }: ISelectOption) => value) ??
        [],

      ...(values[CommonKeys.ValidationStatus]
        ? {
            valid_status: values[CommonKeys.ValidationStatus],
          }
        : {}),

      reviewed_at: values[CommonKeys.DateReviewed]?.isValid()
        ? values[CommonKeys.DateReviewed].format(DateFormats[2])
        : null,

      meta_data:
        transformMetaDataToRequestBody(getValues(), {
          subLayer: parentNodeLayer as SubLayerTypes,
          asset,
        }) ?? {},

      y: position.y,
      x: position.x,

      qnous_ref:
        values[CommonKeys.QnousRef]?.map(({ value }: ISelectOption) => value) ??
        [],

      nist:
        values[CommonKeys.NistRef]?.map(({ value }: ISelectOption) => value) ??
        [],

      iso:
        values[CommonKeys.IsoRef]?.map(({ value }: ISelectOption) => value) ??
        [],

      ism:
        values[CommonKeys.IsmRef]?.map(({ value }: ISelectOption) => value) ??
        [],

      description: values[CommonKeys.Description] || null,

      ...add(isPartnersEnabled, {
        partners: (values[CommonKeys.Partners] ?? []).map(
          ({ value }: ISelectOption) => value,
        ),
      }),

      ...(selectedAssetId && { asset_id: selectedAssetId }),

      ...(values[AssetKeys.BusinessAttributes]
        ? {
            businessAttributes: (
              (values[AssetKeys.BusinessAttributes] ?? []) as ISelectOption[]
            ).map(({ value, label }: ISelectOption) => ({
              id: value as string,
              name: label,
            })),
          }
        : {}),

      ...add(CiaSubLayers.includes(parentNodeLayer as any), {
        cia: prepareCia({
          assurance_level: values[CommonKeys.Assurance],
          coverage: values[CommonKeys.Coverage],
          applicability: values[CommonKeys.Applicability],
        }),
      }),
    };

    return nodeBody;
  };

  const handleCreateNode = async (
    nodeBody: IUpdateNodeRequest,
    subLayerId: SubLayerTypes,
  ): Promise<NodeDTO | undefined> => {
    const type = getNodeTypeTypeByForSubLayer(subLayerId);
    const isAssetSubLayer = AssetsSubLayerTypes.includes(subLayerId);

    const createNodeBody: ICreateNodeRequest = {
      ...nodeBody,
      projectId,
      type,
      ...(isAssetSubLayer && selectedAssetId
        ? { parent_asset: selectedAssetId }
        : {}),
    };

    const response = await createNode(createNodeBody).unwrap();

    // Looking for created node (sorted nodes by updated_at to get the newest one first)
    const createdNode = [...response.nodes]
      .sort((a: NodeDTO, b: NodeDTO) =>
        dayjs(a.updated_at).isBefore(dayjs(b.updated_at)) ? 1 : -1,
      )
      .find(({ name }: NodeDTO) => name === nodeBody.name);

    if (isNewAssetMode) {
      dispatch(nodesApi.util.invalidateTags([QueryTags.AssetsList]));
      selectAsset(createdNode?.id ?? '');
      resetNewAssetMode();
    }

    if (createdNode) {
      checkIsEntityBecameHidden(createdNode);
    }

    saveResponseToRedux(response);
    return createdNode;
  };

  const handleSave = async (options?: { fromShortcut?: boolean }) => {
    const fromShortcut = Boolean(options?.fromShortcut);

    try {
      const resolvedNode =
        resolveCanvasNodeForSave() ?? (sourceNodeFromHook as Node | undefined);

      if (!resolvedNode?.parentNode) {
        throw new Error('Active node not found or has no parent node');
      }

      const { id, position, parentNode } = resolvedNode;

      // Delete owners that were marked for deletion
      const formValues = getValues();

      const ownersToDelete =
        ((formValues as Record<string, unknown>).ownersToDelete as string[]) ||
        [];

      if (ownersToDelete.length > 0) {
        await Promise.all(
          ownersToDelete.map((owner) =>
            removeOwner({ projectId, owner }).unwrap(),
          ),
        );
      }

      const nodeBody = prepareDraftBody(id, position, resolvedNode);
      const displayName = nodeBody.name || 'entity';

      if (editMode) {
        await onUpdate(nodeBody, { closeDrawer: !fromShortcut });

        setActiveNode(id);
        setHasChanges(false);

        emitNodeSaved(id, 'update');
        const skipProjectNodesInvalidation =
          isInsightsMode && insightsTab === Tabs.ModelEvolution;
        dispatch(
          projectsApi.util.invalidateTags(
            skipProjectNodesInvalidation
              ? [QueryTags.Project]
              : [QueryTags.Project, QueryTags.ProjectNodes],
          ),
        );
        showToast(
          fromShortcut
            ? 'Changes saved'
            : `The ${displayName} has been successfully updated.`,
          'success',
        );
        return;
      }

      const createdDto = await handleCreateNode(
        nodeBody,
        parentNode as SubLayerTypes,
      );

      dispatch(
        projectsApi.util.invalidateTags([
          QueryTags.Project,
          QueryTags.ProjectNodes,
        ]),
      );
      setHasChanges(false);

      if (createdDto?.id) {
        setActiveNode(createdDto.id);
      }

      if (fromShortcut && createdDto?.id) {
        const layerId = subLayerParent[parentNode as SubLayerTypes];
        const editDrawerType = layerId && EditNodeDrawerMap[layerId];
        if (editDrawerType) {
          setDrawer({ type: editDrawerType });
        } else {
          hideDrawer();
        }
      } else {
        hideDrawer();
      }

      emitNodeSaved(id, 'create');
      showToast(
        fromShortcut
          ? 'Changes saved'
          : `The ${displayName} has been successfully created.`,
        'success',
      );
    } catch (error: any) {
      const message = error.message || parseErrorResponse(error);

      if (error.data?.field && error.data.field in getValues()) {
        return setError(error.data.field, { message });
      }

      showToast(message, 'error');
    }
  };

  return {
    handleSave,
    isLoading: isCreating,
  };
};

export default useNodeSave;
